import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SeverityBadge, ReviewBadge, StatusPill, Variance } from "../components/badges";
import { num, fmtDate, pct, sevRail } from "../lib/format";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { FileSearch, Fingerprint, History, Sparkles } from "lucide-react";
import { AuditEvents } from "../components/AuditEvents";

const REVIEW_OPTIONS = ["NEW", "ACKNOWLEDGED", "EXPLAINED", "CORRECTED", "DISMISSED"];

export default function FindingDetail() {
  const { findingId } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["finding", findingId],
    queryFn: async () => (await api.get(`/findings/${findingId}`)).data,
  });

  const [status, setStatus] = useState("NEW");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (data?.finding) {
      setStatus(data.finding.review_status || "NEW");
      setNote(data.finding.reviewer_note || "");
    }
  }, [data]);

  const nav = useNavigate();
  const mutation = useMutation({
    mutationFn: async (overrideStatus) =>
      (await api.patch(`/findings/${findingId}/review`, {
        review_status: overrideStatus || status,
        reviewer_note: note,
      })).data,
    onSuccess: (_d, overrideStatus) => {
      if (overrideStatus) setStatus(overrideStatus);
      toast.success("Review updated");
      qc.invalidateQueries({ queryKey: ["finding", findingId] });
      qc.invalidateQueries({ predicate: (q) => ["run", "client", "client-head", "emp", "worklist"].includes(q.queryKey?.[0]) });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"),
  });

  const saveAndNext = async () => {
    try {
      await mutation.mutateAsync();
      const clientGuid = data?.finding?.payroll_client_GUID;
      const wl = (await api.get(`/worklist?client=${clientGuid}&limit=50`)).data;
      const next = (wl.items || []).find((i) => i.check_result_GUID !== findingId);
      if (next) nav(`/findings/${next.check_result_GUID}`);
      else toast.success("No more open findings for this customer.");
    } catch (e) {
      // mutation.onError already shows a toast for save failures; log the worklist-fetch/navigation path for debugging
      console.error("saveAndNext failed", e);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "e") { e.preventDefault(); mutation.mutate("EXPLAINED"); }
      else if (k === "a") { e.preventDefault(); mutation.mutate("ACKNOWLEDGED"); }
      else if (k === "d") { e.preventDefault(); mutation.mutate("DISMISSED"); }
      else if (k === "n") { e.preventDefault(); saveAndNext(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, note, data]);

  const linkMutation = useMutation({
    mutationFn: async (eventGuid) =>
      (await api.patch(`/findings/${findingId}/review`, {
        review_status: eventGuid ? "EXPLAINED" : status,
        reviewer_note: note,
        linked_audit_event_GUID: eventGuid,
      })).data,
    onSuccess: (_d, eventGuid) => {
      if (eventGuid) setStatus("EXPLAINED");
      toast.success(eventGuid ? "Cause linked · marked Explained" : "Cause unlinked");
      qc.invalidateQueries({ queryKey: ["finding", findingId] });
      qc.invalidateQueries({ predicate: (q) => ["run", "client", "client-head", "emp"].includes(q.queryKey?.[0]) });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Link failed"),
  });

  if (isLoading)
    return (
      <Layout>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-96 rounded-md" />
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="rounded-md border border-neg/30 bg-neg/5 p-6 text-neg">
          {error.response?.status === 403 ? "You do not have access to this customer." : "Failed to load finding."}
        </div>
      </Layout>
    );

  const { finding, evidence, audit_window, audit_match } = data;
  const changePct = pct(finding.observed_value, finding.prior_value);

  return (
    <Layout>
      <Breadcrumbs
        items={[
          { label: "Portfolio", to: "/" },
          { label: finding.client_name, to: `/clients/${finding.payroll_client_GUID}` },
          finding.payroll_run_GUID
            ? { label: `Register ${finding.run_label}`, to: `/runs/${finding.payroll_run_GUID}` }
            : { label: "Register" },
          { label: finding.check_code },
        ]}
      />

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <StatusPill status={finding.result_status} />
        {finding.result_status === "FAIL" && <SeverityBadge severity={finding.severity} />}
        <ReviewBadge status={finding.review_status} />
      </div>
      <h1 className="mt-2 font-heading text-4xl font-black">{finding.check_name}</h1>
      <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground border-b border-border pb-5">
        {finding.check_code} · {finding.result_grain} · {finding.check_origin}
        {finding.current_employee_name ? ` · ${finding.current_employee_name}` : ""}
        {(finding.provider_employee_id || finding.provider_file_number_as_printed)
          ? ` · ID ${finding.provider_employee_id || finding.provider_file_number_as_printed}` : ""}
      </p>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* Left: variance + reason */}
        <div className="lg:col-span-2 space-y-6">
          {/* Variance strip */}
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <div className={`h-1 ${finding.delta_value > 0 ? "bg-pos" : finding.delta_value < 0 ? "bg-neg" : "bg-muted"}`} />
            <div className="p-6">
              <h2 className="font-heading text-lg font-bold mb-5">Variance</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-10 gap-y-6">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Observed</div>
                  <div className="mt-1 font-mono text-2xl lg:text-3xl font-black tracking-tight tabular-nums whitespace-nowrap">{num(finding.observed_value, 2)}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Prior</div>
                  <div className="mt-1 font-mono text-2xl lg:text-3xl font-black tracking-tight tabular-nums whitespace-nowrap text-muted-foreground">{num(finding.prior_value, 2)}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Delta</div>
                  <div className={`mt-1 flex items-baseline gap-1 font-mono text-2xl lg:text-3xl font-black tracking-tight tabular-nums whitespace-nowrap ${finding.delta_value > 0 ? "text-pos" : finding.delta_value < 0 ? "text-neg" : "text-muted-foreground"}`}>
                    <span className="text-lg">{finding.delta_value > 0 ? "+" : finding.delta_value < 0 ? "−" : ""}</span>
                    {num(Math.abs(finding.delta_value ?? 0), 2)}
                  </div>
                  {changePct != null && (
                    <div className={`font-mono text-xs mt-0.5 ${changePct >= 0 ? "text-pos" : "text-neg"}`}>
                      {changePct >= 0 ? "+" : ""}{num(changePct, 1)}%
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Threshold</div>
                  <div className="mt-1 font-mono text-2xl lg:text-3xl font-black tracking-tight tabular-nums whitespace-nowrap text-warn">{num(finding.threshold_value, 2)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Root cause */}
          <div className={`rounded-md border border-border border-l-4 ${finding.result_status === "FAIL" ? sevRail(finding.severity) : "border-l-transparent"} bg-card p-6`}>
            <div className="flex items-center gap-2 mb-3">
              <FileSearch size={18} className="text-muted-foreground" />
              <h2 className="font-heading text-lg font-bold">Root Cause Analysis</h2>
            </div>
            {finding.result_message && (
              <p className="text-base font-medium">{finding.result_message}</p>
            )}
            {finding.result_reason && (
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
                {finding.result_reason}
              </p>
            )}
            {finding.check_description && (
              <p className="mt-4 text-xs text-muted-foreground">
                <span className="font-semibold uppercase tracking-wide">Check logic: </span>
                {finding.check_description}
              </p>
            )}
          </div>

          {/* Evidence */}
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-4">
              <Fingerprint size={18} className="text-muted-foreground" />
              <h2 className="font-heading text-lg font-bold">Evidence Trail</h2>
            </div>
            {evidence.length === 0 ? (
              <div className="px-6 pb-6 text-sm text-muted-foreground">No evidence records linked to this finding.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Role</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Row ID</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {evidence.map((ev, i) => (
                    <TableRow key={ev.source_row_GUID ?? i}>
                      <TableCell>
                        <span className="rounded-sm border border-border bg-muted px-2 py-0.5 text-xs font-semibold uppercase">
                          {ev.evidence_role}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{ev.source_schema_name}.{ev.source_table_name}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground truncate max-w-[180px]">{ev.source_row_GUID}</TableCell>
                      <TableCell className="text-right font-mono">{ev.evidence_value != null ? num(ev.evidence_value, 2) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Employee audit tied to this finding */}
          {finding.subject_employee_GUID && (
            <EmployeeAuditPanel
              clientId={finding.payroll_client_GUID}
              employeeId={finding.subject_employee_GUID}
              employeeName={finding.current_employee_name}
              window={audit_window}
              match={audit_match}
              linkedGuid={finding.linked_audit_event_GUID}
              onLink={(guid) => linkMutation.mutate(guid)}
              linkPending={linkMutation.isPending}
            />
          )}
        </div>

        {/* Right: reviewer workflow */}
        <div className="space-y-6">
          <div className="rounded-md border border-border bg-card p-6 sticky top-24">
            <h2 className="font-heading text-lg font-bold mb-4">Reviewer Workflow</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Review status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="review-status-select" className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REVIEW_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>{o.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Reviewer note</label>
                <Textarea
                  data-testid="review-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Explain the variance or record the correction…"
                  className="mt-1.5 min-h-[120px]"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  data-testid="quick-explained"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate("EXPLAINED")}
                  className="flex items-center justify-center gap-1 rounded-md border border-pos/40 bg-pos/10 px-2 py-2 text-xs font-bold uppercase tracking-wide text-pos transition-colors hover:bg-pos/20 disabled:opacity-50"
                >
                  Explained <kbd className="rounded bg-pos/20 px-1 text-[10px]">E</kbd>
                </button>
                <button
                  type="button"
                  data-testid="quick-acknowledged"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate("ACKNOWLEDGED")}
                  className="flex items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                >
                  Ack <kbd className="rounded bg-muted px-1 text-[10px]">A</kbd>
                </button>
                <button
                  type="button"
                  data-testid="quick-dismissed"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate("DISMISSED")}
                  className="flex items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                >
                  Dismiss <kbd className="rounded bg-muted px-1 text-[10px]">D</kbd>
                </button>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="review-save"
                  variant="outline"
                  className="flex-1"
                  onClick={() => mutation.mutate()}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? "Saving…" : "Save"}
                </Button>
                <Button
                  data-testid="review-save-next"
                  className="flex-1"
                  onClick={saveAndNext}
                  disabled={mutation.isPending}
                >
                  Save &amp; next
                </Button>
              </div>
              <p className="text-center text-[11px] text-muted-foreground">
                Shortcuts: <b>E</b> explained · <b>A</b> ack · <b>D</b> dismiss · <b>N</b> save &amp; next
              </p>
              {finding.reviewed_by && (
                <p className="text-xs text-muted-foreground">
                  Last reviewed by {finding.reviewed_by}
                  {finding.reviewed_at ? ` on ${fmtDate(finding.reviewed_at)}` : ""}
                </p>
              )}
              {finding.linked_audit_event_GUID && (
                <div
                  data-testid="linked-cause-indicator"
                  className="flex items-center gap-2 rounded-md border border-pos/30 bg-pos/[0.06] px-3 py-2 text-xs font-medium text-pos"
                >
                  <Sparkles size={14} className="shrink-0" />
                  Explaining audit change linked below.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function EmployeeAuditPanel({ clientId, employeeId, employeeName, window, match, linkedGuid, onLink, linkPending }) {
  const [mode, setMode] = useState(window ? "window" : "all");
  const hasWindow = !!window;
  const useWindow = hasWindow && mode === "window";
  const hasMatch = !!(match && match.event_guids && match.event_guids.length);

  return (
    <div className="rounded-md border border-border bg-card p-6" data-testid="employee-audit-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-2">
          <History size={18} className="text-muted-foreground" />
          <h2 className="font-heading text-lg font-bold">Employee Audit — Who Changed What</h2>
        </div>
        {hasWindow && (
          <div className="inline-flex rounded-md border border-border overflow-hidden text-xs font-semibold">
            <button
              data-testid="audit-mode-window"
              onClick={() => setMode("window")}
              className={`px-3 py-1.5 transition-colors ${useWindow ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              Between registers
            </button>
            <button
              data-testid="audit-mode-all"
              onClick={() => setMode("all")}
              className={`px-3 py-1.5 transition-colors ${!useWindow ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              All history
            </button>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground mb-3">
        {hasWindow ? (
          useWindow ? (
            <>
              Changes for <span className="font-semibold text-foreground">{employeeName}</span> recorded between
              register <span className="font-mono">{window.prior_label}</span> ({fmtDate(window.prior_pay_date)}) and
              register <span className="font-mono">{window.current_label}</span> ({fmtDate(window.current_pay_date)}) —
              a likely source of this variance. Click any row to drill into the exact before/after values.
            </>
          ) : (
            <>All recorded changes for <span className="font-semibold text-foreground">{employeeName}</span>. Highlighted rows fall between the two compared registers.</>
          )
        ) : (
          <>All recorded changes for <span className="font-semibold text-foreground">{employeeName}</span>.</>
        )}
      </p>

      {hasMatch && (
        <div
          data-testid="audit-match-callout"
          className="mb-4 flex items-start gap-3 rounded-md border border-warn/40 bg-warn/[0.08] px-4 py-3"
        >
          <Sparkles size={18} className="mt-0.5 shrink-0 text-warn" />
          <div className="text-sm">
            <span className="font-semibold text-foreground">Likely cause found.</span>{" "}
            {match.event_guids.length === 1 ? "One change" : `${match.event_guids.length} changes`} in this window{" "}
            {match.event_guids.length === 1 ? "matches" : "match"}{" "}
            {match.subject_qualifier ? (
              <span className="font-mono font-semibold">{match.subject_qualifier}</span>
            ) : "this finding"}{" "}
            {match.reasons?.[match.primary_guid] ? (
              <span className="text-muted-foreground">({match.reasons[match.primary_guid]})</span>
            ) : null}
            . It's flagged <span className="font-semibold text-warn">below</span>.
          </div>
        </div>
      )}

      <AuditEvents
        clientId={clientId}
        employeeId={employeeId}
        limit={200}
        dateFrom={useWindow ? window.from : undefined}
        dateTo={useWindow ? window.to : undefined}
        windowFrom={hasWindow ? window.from : undefined}
        windowTo={hasWindow ? window.to : undefined}
        matchGuids={hasMatch ? match.event_guids : []}
        matchReasons={hasMatch ? match.reasons : {}}
        primaryGuid={hasMatch ? match.primary_guid : undefined}
        linkedGuid={linkedGuid}
        onLink={onLink}
        linkPending={linkPending}
        emptyText={useWindow
          ? "No employee changes were recorded between these two registers."
          : "No audit changes recorded for this employee."}
      />
    </div>
  );
}
